// components/getStarted/GetStartedFlow.jsx
import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import RegistrationForm from '../forms/registration';
import CreateTeamFormFields from '../forms/CreateTeamFormFields';
import { getAuth } from 'firebase/auth';

export default function GetStartedFlow({ onClose }) {
  const [step, setStep] = React.useState(0);           // 0=choose, 1=register, 2=create team
  const [choice, setChoice] = React.useState(null);    // 'join' | 'start' | 'migrate'
  const navigate = useNavigate();

  // Lock body scroll while open
  React.useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Close on Escape
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleChoice = (c) => {
    setChoice(c);
    if (c === 'join') {
      setStep(1); // always register first
      return;
    }
    // For start/migrate, skip registration if already logged in
    const user = getAuth().currentUser;
    setStep(user ? 2 : 1);
  };

  const target = document?.body || null;
  if (!target) return null;

  return createPortal(
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Get Started">
      <div className="modal-content" style={{ width: 'min(900px, 96vw)', paddingBottom: 0 }}>
        {/* Header */}
        <div className="modal-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
          <h3 className="modal-title">Get Started</h3>
          <button className="button button-secondary" onClick={onClose}>Close</button>
        </div>

        {/* Step 0: choose path */}
        {step === 0 && (
          <div className="modal-body" style={{ paddingBottom: 20 }}>
            <div className="text-center" style={{ marginBottom: 16 }}>
              <h4 className="text-2xl font-bold">What would you like to do?</h4>
              <p className="text-gray-600">Choose the path that fits you best.</p>
            </div>
            <div className="dashboard-grid" style={{ padding: 0 }}>
              <ChoiceCard emoji="🙋" title="Join a club" desc="Create your account, then find and join a local club." onClick={() => handleChoice('join')} />
              <ChoiceCard emoji="✨" title="Start a club" desc="Set up a brand-new club in minutes." onClick={() => handleChoice('start')} />
              <ChoiceCard emoji="🚀" title="Bring your club online" desc="Migrate your existing club to Member Nest." onClick={() => handleChoice('migrate')} />
            </div>
          </div>
        )}

        {/* Step 1: registration (used for all three paths) */}
        {step === 1 && (
          <div className="modal-body">
            <div className="form-card card--elevated border rounded-2xl" style={{ padding: '1.25rem' }}>
              <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
                <div>
                  <h4 className="text-2xl font-bold mb-1">
                    {choice === 'join' ? 'Create your account' : 'First, let’s set up your account'}
                  </h4>
                  <p className="text-gray-600">
                    {choice === 'join'
                      ? 'Then we’ll take you straight to club search.'
                      : 'Next, we’ll set up your club.'}
                  </p>
                  <p className="text-gray-500" style={{ fontSize: '0.9rem', marginTop: 6 }}>
                    You can finish profile details later.
                  </p>
                </div>
                <button className="link" onClick={() => setStep(0)}>← Back</button>
              </div>

              <hr className="divider" style={{ margin: '0.75rem 0 1rem', border: 0, borderTop: '1px solid #eee' }} />

              <RegistrationForm
                onSuccess={() => {
                  if (choice === 'join') {
                    navigate('/teams/search');
                    onClose?.();
                  } else {
                    setStep(2); // proceed to create team
                  }
                }}
              />
            </div>
          </div>
        )}

        {step === 2 && (choice === 'start' || choice === 'migrate') && (
  <div className="modal-body">
    <div className="form-card card--elevated border rounded-2xl" style={{ padding: '1.25rem' }}>
      <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
        <div>
          <h4 className="text-2xl font-bold mb-1">
            {choice === 'start' ? 'Create your new club' : 'Bring your club online'}
          </h4>
          <p className="text-gray-600">
            Add a few details to get your club space created. You can change these later.
          </p>
        </div>
        <button className="button button-secondary px-4 py-2 text-sm" onClick={() => setStep(1)}>
          ← Back
        </button>
      </div>

      <hr className="divider" style={{ margin: '0.75rem 0 1rem', border: 0, borderTop: '1px solid #eee' }} />


     <CreateTeamFormFields
       onCancel={() => setStep(1)}
       onSuccess={(teamId) => {
         navigate(`/teams/${teamId}`, { replace: true, state: { onboarding: true } });
         onClose?.();
       }}
     />
            </div>
          </div>
        )}
      </div>
    </div>,
    target
  );
}

function ChoiceCard({ emoji, title, desc, onClick }) {
  return (
    <button
      onClick={onClick}
      className="choice-card focus:outline-none"
    >
      <div className="choice-emoji">{emoji}</div>
      <div className="choice-title">{title}</div>
      <div className="choice-desc">{desc}</div>
    </button>
  );
}
